import { API_BASE_URL } from "src/config/api";
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import step1 from '../../../Assets/document/proxyStep1.gif';
import step2 from '../../../Assets/document/proxyStep2.gif';
import step3 from '../../../Assets/document/proxyStep3.gif';
import step4 from '../../../Assets/document/proxyStep4.gif';
import step5 from '../../../Assets/document/proxyStep5.gif';

const listItemStyle = {
  marginBottom: '0.75rem',
};

// Reusable code block with copy functionality
const CodeBlock = ({ code }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      <Box
        component="pre"
        sx={{
          backgroundColor: '#f4f4f4',
          p: 2,
          borderRadius: 1,
          overflowX: 'auto',
          width: '100%',
          maxWidth: '100%',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '0.875rem',
        }}
      >
        {code}
      </Box>
      <Tooltip title="Copy to clipboard">
        <IconButton
          onClick={handleCopy}
          aria-label="Copy command to clipboard"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
          }}
          size="small"
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const PushProxClientSetup = () => {
  const [ hostname, setHostname ] = useState(null)
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const userId = localStorage.getItem('userId');
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      const response = await axios.get(`${API_BASE_URL}/generateHostname?userId=${userId}`, config);
      setHostname(response.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [fetchData]);

  const cmd = [
    "sudo docker run -d",
    hostname ? `--hostname ${hostname}` : null,
    "--network host",
    "--name pushprox-client",
    hostname ? `--add-host ${hostname}:0.0.0.0` : null,
    "--entrypoint app/pushprox-client",
    "prometheuscommunity/pushprox",
    "--proxy-url=http://13.232.108.253:8080"
  ]
    .filter(Boolean)   // remove null/undefined
    .join(" ");        // join into single command string

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 2 }}>
      {/* Objective */}
      <Typography variant="h4" gutterBottom>
        Objective
      </Typography>
      <Typography paragraph>
        Monitoring servers behind NAT or private IPs can be challenging, especially when Prometheus needs to scrape metrics from those servers. That’s where PushProx comes in.
      </Typography>
      <Typography paragraph>
        In this guide, we’ll walk through setting up the PushProx client using Docker on a private server, enabling WatchNod to collect metrics even from NAT-restricted environments.
      </Typography>

      {/* Prerequisites */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Prerequisites
      </Typography>
      <ul style={{ paddingLeft: '1.25rem' }}>
        <li>
          <Typography variant="body1">
            Docker is installed on your server. If not, visit the{' '}
            <a href="https://docs.docker.com/get-docker/" target="_blank" rel="noopener noreferrer">
              Docker official installation guide
            </a>.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Node Exporter is up and running. If not, visit the installation guide from {' '}
            <a href="/dashboard/documentation?tab=2&section=itemB" rel="noopener noreferrer">
              here
            </a>.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            WatchNod PushProx Server IP is reachable by your private server. You can ping the IP to verify connectivity.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            <strong>Server IP:</strong> <code>13.232.108.253</code>
          </Typography>
        </li>
      </ul>

      {/* Video Tutorial */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Watch the Setup Video
      </Typography>
      <Box display="flex" justifyContent="center" my={2}>
        <iframe
          width="100%"
          height="550"
          src="https://www.youtube.com/embed/x8-QnC42eOA"
          title="Push Prox Setup Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: '8px' }}
        />
      </Box>

      {/* Step 1 */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Step 1: Pull the PushProx Docker Image
      </Typography>
      <Typography paragraph>
        On your private server, run the following command:
      </Typography>
      <CodeBlock code="docker pull prometheuscommunity/pushprox" />

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step1}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>

      {/* Step 2 */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Step 2: Run the PushProx Client Container
      </Typography>
      <Typography paragraph>
        Start the PushProx client using the following command:
      </Typography>
      <CodeBlock code={cmd} />

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step2}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>

      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Explanation:
      </Typography>
      <ul style={{ paddingLeft: '1.25rem' }}>
        <li style={listItemStyle}><Typography variant="body1"><code>-d</code>: Run in detached mode (in the background).</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--name pushprox-client</code>: Name of the container.</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--network host</code>: Use the host network.</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--entrypoint /app/pushprox-client</code>: Override default entrypoint to run the PushProx client binary.</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>prometheuscommunity/pushprox</code>: Docker image used.</Typography></li>
        <li style={listItemStyle}><Typography variant="body1"><code>--proxy-url=http://13.232.108.253:8080</code>: URL of the public WatchNod PushProx server where this client registers.</Typography></li>
      </ul>

      {/* Step 3 */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Step 3: Verify the Container is Running
      </Typography>
      <Typography paragraph>Run the following command:</Typography>
      <CodeBlock code="docker ps" />

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step3}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>

      <Typography paragraph>
        You should see one called <code>pushprox-client</code>. If it’s listed, it’s running correctly.
      </Typography>

      {/* Step 4 */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Step 4: Get the Hostname/FQDN to Add in WatchNod
      </Typography>
      <Typography paragraph>
        To confirm what hostname is being sent to the WatchNod PushProx server, run:
      </Typography>
      <CodeBlock code="docker logs pushprox-client" />

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step4}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>


      <Typography paragraph>
        Look for the line that includes: <code>fqdn="your_fqdn_name"</code>
      </Typography>

      {/* Step 5 */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Step 5: Verify Connection to WatchNod PushProx Server
      </Typography>
      <Typography paragraph>
        Run the following command on your private server:
      </Typography>
      <CodeBlock code="curl http://13.232.108.253:8080/clients" />

      <Box display="flex" justifyContent="center" my={2}>
        <img
          src={step5}
          alt="Installation in progress"
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        />
      </Box>


      <Typography paragraph>
        If you see your FQDN listed under <code>targets: ["your_fqdn_name"]</code>, your client is successfully connected.
      </Typography>

      {/* Completion */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        🎉 Done!
      </Typography>
      <Typography paragraph>
        You've successfully installed the PushProx Client on your server.
      </Typography>
    </Box>
  );
};

export default PushProxClientSetup;
